// Lab Sample List - Testing Planning & Tracking
// View and manage all lab test samples
// ============================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLabSample } from '../contexts/LabSampleContext';
import { getLabStatusColor, getPriorityColor } from '../utils/colorHelpers';
import {
    Beaker,
    Plus,
    Search,
    Calendar,
    User,
    Clock,
    AlertCircle,
    TrendingUp,
    ExternalLink,
} from 'lucide-react';

export const LabSampleList: React.FC = () => {
    const { samples, getMetrics } = useLabSample();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('All');
    const [typeFilter, setTypeFilter] = useState<string>('All');
    const [priorityFilter, setPriorityFilter] = useState<string>('All');

    // Filter samples
    const filteredSamples = samples.filter(sample => {
        const matchesSearch =
            sample.sampleNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
            sample.product.toLowerCase().includes(searchTerm.toLowerCase()) ||
            sample.serialNumber?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'All' || sample.status === statusFilter;
        const matchesType = typeFilter === 'All' || sample.sampleType === typeFilter;
        const matchesPriority = priorityFilter === 'All' || sample.priority === priorityFilter;

        return matchesSearch && matchesStatus && matchesType && matchesPriority;
    });

    // Get metrics
    const metrics = getMetrics();

    const getTypeLabel = (type: string) => {
        const labels: Record<string, string> = {
            'test_unit': 'Test Unit',
            'field_trial': 'Field Trial',
            'warranty_return': 'Warranty Return',
            'customer_sample': 'Customer Sample',
            'validation': 'Validation',
        };
        return labels[type] || type;
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                            <Beaker className="w-8 h-8 text-blue-600" />
                            Lab Testing & Tracking
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Sample planning, test scheduling, and results reporting
                        </p>
                    </div>
                    <Link
                        to="/lab/create"
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                    >
                        <Plus className="w-5 h-5" />
                        Submit Sample
                    </Link>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Total Samples</p>
                                <p className="text-2xl font-bold text-gray-900">{metrics.totalSamples}</p>
                            </div>
                            <Beaker className="w-8 h-8 text-gray-400" />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">In Queue</p>
                                <p className="text-2xl font-bold text-yellow-600">{metrics.queuedSamples}</p>
                            </div>
                            <Clock className="w-8 h-8 text-yellow-400" />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Critical</p>
                                <p className="text-2xl font-bold text-red-600">{metrics.criticalSamples}</p>
                            </div>
                            <AlertCircle className="w-8 h-8 text-red-400" />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Avg TAT (days)</p>
                                <p className="text-2xl font-bold text-green-600">{metrics.averageTurnaroundTime}</p>
                            </div>
                            <TrendingUp className="w-8 h-8 text-green-400" />
                        </div>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="flex flex-wrap gap-4 mb-4">
                    <div className="flex-1 min-w-[300px]">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search samples..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>
                    <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="All">All Status</option>
                        <option value="received">Received</option>
                        <option value="queued">Queued</option>
                        <option value="testing">Testing</option>
                        <option value="analysis">Analysis</option>
                        <option value="complete">Complete</option>
                        <option value="on_hold">On Hold</option>
                    </select>
                    <select
                        value={typeFilter}
                        onChange={e => setTypeFilter(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="All">All Types</option>
                        <option value="test_unit">Test Unit</option>
                        <option value="field_trial">Field Trial</option>
                        <option value="warranty_return">Warranty Return</option>
                        <option value="customer_sample">Customer Sample</option>
                        <option value="validation">Validation</option>
                    </select>
                    <select
                        value={priorityFilter}
                        onChange={e => setPriorityFilter(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="All">All Priority</option>
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                    </select>
                </div>
            </div>

            {/* Samples Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Sample
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Type
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Priority
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Due Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Links
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredSamples.map(sample => (
                                <tr key={sample.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div>
                                            <Link
                                                to={`/lab/${sample.id}`}
                                                className="text-blue-600 hover:text-blue-800 font-medium"
                                            >
                                                {sample.sampleNumber}
                                            </Link>
                                            <p className="text-sm text-gray-600 mt-1">{sample.product}</p>
                                            {sample.serialNumber && (
                                                <p className="text-xs text-gray-500">SN: {sample.serialNumber}</p>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="text-sm text-gray-900">{getTypeLabel(sample.sampleType)}</span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span
                                            className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(
                                                sample.priority
                                            )}`}
                                        >
                                            {sample.priority.toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span
                                            className={`px-3 py-1 rounded-full text-xs font-medium ${getLabStatusColor(
                                                sample.status
                                            )}`}
                                        >
                                            {sample.status.replace('_', ' ').toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <Calendar className="w-4 h-4" />
                                            {sample.testDueDate ? new Date(sample.testDueDate).toLocaleDateString() : '-'}
                                        </div>
                                        {sample.assignedTechnician && (
                                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                                                <User className="w-3 h-3" />
                                                {sample.assignedTechnician}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col gap-1 text-xs">
                                            {sample.fracasIncidentId && (
                                                <Link
                                                    to={`/fracas/${sample.fracasIncidentId.split('-')[1]}`}
                                                    className="text-blue-600 hover:underline flex items-center gap-1"
                                                >
                                                    FRACAS-{sample.fracasIncidentId.split('-')[1]}
                                                    <ExternalLink className="w-3 h-3" />
                                                </Link>
                                            )}
                                            {sample.eightDReportId && (
                                                <Link
                                                    to={`/8d/${sample.eightDReportId.split('-')[1]}`}
                                                    className="text-purple-600 hover:underline flex items-center gap-1"
                                                >
                                                    8D-{sample.eightDReportId.split('-')[1]}
                                                    <ExternalLink className="w-3 h-3" />
                                                </Link>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <Link
                                            to={`/lab/${sample.id}`}
                                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                        >
                                            View Details →
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {filteredSamples.length === 0 && (
                    <div className="text-center py-12">
                        <Beaker className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600">No samples found matching your filters.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LabSampleList;
